import type {BusinessCountry} from '@/types';

const Row = ({label, value}: {label: string; value: string}) => (
  <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
    <p className="text-slate-500 text-sm dark:text-slate-100">{label}</p>
    <p className="text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 break-words">
      {value}
    </p>
  </div>
);

/**
 * Read-only reference data for one business country.
 *
 * `meta.kyc` / `meta.kyb` are summarized by count rather than listed: each
 * `idOptions` entry is a full document-capture config (regex, value type,
 * length bounds) that belongs in an editor, and the number is what tells ops
 * whether a corridor is configured at all.
 */
const Summary = ({item}: {item: BusinessCountry}) => {
  const codes = item?.meta?.codes;

  return (
    <div className="flex flex-col gap-3 mt-4">
      <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-200">
        {item?.name} Details
      </h3>
      <div className="overflow-x-auto">
        <Row label="Name" value={item?.name ?? 'Not set'} />
        <Row
          label="Code"
          value={`${item?.code ?? ''} ${codes?.flag ?? ''}`.trim() || 'Not set'}
        />
        <Row label="ISO3" value={codes?.iso3 ?? 'Not set'} />
        <Row label="Currency" value={codes?.currencyCode ?? 'Not set'} />
        <Row label="Currency name" value={codes?.currencyName ?? 'Not set'} />
        <Row label="Phone prefix" value={codes?.phonePrefix ?? 'Not set'} />
        <Row
          label="Capabilities"
          value={item?.meta?.capabilities?.join(', ') || 'Not set'}
        />
        <Row
          label="KYC document options"
          value={String(item?.meta?.kyc?.idOptions?.length ?? 0)}
        />
        <Row
          label="Captures KYC address"
          value={item?.meta?.kyc?.shouldCaptureAddress ? 'Yes' : 'No'}
        />
        <Row
          label="KYB document options"
          value={String(item?.meta?.kyb?.idOptions?.length ?? 0)}
        />
        <Row
          label="Required KYB documents"
          value={item?.meta?.kyb?.requiredKYC?.join(', ') || 'None'}
        />
      </div>
    </div>
  );
};

export default Summary;
